import React, { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import Cookies from 'js-cookie';
import './Header.css';
import CartHeader from './CartHeader';
import FaveHeader from './FaveHeader';
import ProfileHeader from './ProfileHeader';
import store, { setAuthenticated, setInitCart, setInitFavourite } from '../../store/store';

const SELECTED_SECTION_COOKIE = 'selectedSection';
const STOCK_CATALOG_KIND = 0;
const PREORDER_CATALOG_KIND = 1;

const getTabRouteSlug = (tab) => tab?.RouteSlug || tab?.Slug || tab?.Sename;
const normalizeUrl = (url) => {
    if (!url) {
        return '';
    }

    if (/^https?:\/\//i.test(url) || url.startsWith('/')) {
        return url;
    }

    return `/${url}`;
};

const normalizeCatalogKindValue = (value) => {
    if (value === PREORDER_CATALOG_KIND || value === '1' || value === 'preorder' || value === 'aproduct') {
        return PREORDER_CATALOG_KIND;
    }

    if (value === STOCK_CATALOG_KIND || value === '0' || value === 'product' || value === 'stock' || value === 'inventory') {
        return STOCK_CATALOG_KIND;
    }

    return undefined;
};

const getCatalogKindFromPath = (pathname) => {
    const segments = pathname.split('/').filter(Boolean);
    const shoppingIndex = segments.indexOf('shopping');

    if (shoppingIndex < 0) {
        return STOCK_CATALOG_KIND;
    }

    return segments[shoppingIndex + 1] === 'instock' || segments[shoppingIndex + 1] === 'product'
        ? STOCK_CATALOG_KIND
        : PREORDER_CATALOG_KIND;
};

const getSectionFromPath = (pathname) => {
    const segments = pathname.split('/').filter(Boolean);
    const shoppingIndex = segments.indexOf('shopping');

    if (shoppingIndex < 0) {
        return undefined;
    }

    const shoppingSegments = segments.slice(shoppingIndex + 1);
    const firstSegment = shoppingSegments[0];

    if (firstSegment === 'preorder' || firstSegment === 'aproduct' || firstSegment === 'product') {
        return shoppingSegments[1]?.toLowerCase();
    }

    return firstSegment?.toLowerCase();
};

const getCategoryPathFromPath = (pathname) => {
    const segments = pathname.split('/').filter(Boolean);
    const shoppingIndex = segments.indexOf('shopping');

    if (shoppingIndex < 0) {
        return '';
    }

    const shoppingSegments = segments.slice(shoppingIndex + 1);
    const firstSegment = shoppingSegments[0];
    const categorySegments = (firstSegment === 'preorder' || firstSegment === 'aproduct' || firstSegment === 'product'
        ? shoppingSegments.slice(1)
        : shoppingSegments
    ).filter((segment) => segment !== 'items');

    return categorySegments.join('/').toLowerCase();
};

const buildCategoryMap = (tabs) =>
    tabs.reduce((acc, item, index) => {
        const routeSlug = getTabRouteSlug(item);
        if (!routeSlug) {
            return acc;
        }

        acc[index] = routeSlug;
        acc[routeSlug] = index;
        return acc;
    }, {});

const ensureClothingSubTab = (subTabs, section, catalogKind) => {
    if (!section || subTabs.some((tab) => (tab?.CategorySename || '').toLowerCase() === 'clothing')) {
        return subTabs;
    }

    const clothingTab = {
        CategorySename: 'clothing',
        RouteSlug: `${section}/clothing`,
        Title: 'Одежда',
        Url: catalogKind === STOCK_CATALOG_KIND
            ? `/shopping/product/${section}/clothing/items`
            : `/shopping/${section}/clothing/items`,
    };
    const insertIndex = section === 'women'
        ? Math.max(subTabs.findIndex((tab) => tab?.CategorySename === 'bags') + 1, 0)
        : 0;

    return [...subTabs.slice(0, insertIndex), clothingTab, ...subTabs.slice(insertIndex)];
};

const prependBrandsSubTab = (subTabs, section, catalogKind) => {
    if (!section || subTabs.some((tab) => (tab?.CategorySename || tab?.RouteSlug || '').toLowerCase().endsWith('brands'))) {
        return subTabs;
    }

    return [
        {
            CategorySename: 'brands',
            RouteSlug: `${section}/brands`,
            Title: 'Бренды',
            Url: catalogKind === STOCK_CATALOG_KIND
                ? `/shopping/product/${section}/items`
                : `/shopping/${section}/items`,
        },
        ...subTabs,
    ];
};

const Header = ({ data }) => {
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [headerHidden, setHeaderHidden] = useState(false);
    const lastScrollY = useRef(0);
    const allTabs = data?.Tabs?.$values || [];
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const activeCatalogKind = getCatalogKindFromPath(pathname);
    const tabs = allTabs.filter((tab) => {
        const tabCatalogKind = normalizeCatalogKindValue(tab?.CatalogKind);
        return tabCatalogKind === undefined || tabCatalogKind === activeCatalogKind;
    });
    const categories = buildCategoryMap(tabs);
    const sectionFromPath = getSectionFromPath(pathname);
    const categoryPathFromPath = getCategoryPathFromPath(pathname);
    const selectedCookieValue = Cookies.get(SELECTED_SECTION_COOKIE);
    const cookieSection = selectedCookieValue !== undefined ? categories[selectedCookieValue] : undefined;
    const selectedCategory = sectionFromPath || cookieSection || getTabRouteSlug(tabs[0]) || null;
    const selectedTab = tabs.find((tab) => getTabRouteSlug(tab) === selectedCategory) || tabs[0];
    const availableSubTabs = (selectedTab?.SubTabs?.$values || []).filter((subtab) => {
        const subtabCatalogKind = normalizeCatalogKindValue(subtab?.CatalogKind);
        return subtabCatalogKind === undefined || subtabCatalogKind === activeCatalogKind;
    });
    const selectedSectionSlug = getTabRouteSlug(selectedTab);
    const selectedSubTabs = prependBrandsSubTab(
        ensureClothingSubTab(availableSubTabs, selectedSectionSlug, activeCatalogKind),
        selectedSectionSlug,
        activeCatalogKind
    );

    useEffect(() => {
        store.dispatch(setInitCart(data?.CartIds?.$values || []));
        store.dispatch(setAuthenticated(Boolean(data?.IsAuthenticated)));
        store.dispatch(setInitFavourite(data?.FaveIds?.$values || []));
    }, [data]);

    useEffect(() => {
        window.categories = categories;
    }, [categories]);

    useEffect(() => {
        let ticking = false;
        const updateHeader = () => {
            const currentScrollY = Math.max(window.scrollY, 0);
            const delta = currentScrollY - lastScrollY.current;

            if (currentScrollY < 24) {
                setHeaderHidden(false);
            } else if (Math.abs(delta) > 5) {
                setHeaderHidden(delta > 0);
            }

            lastScrollY.current = currentScrollY;
            ticking = false;
        };
        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(updateHeader);
                ticking = true;
            }
        };

        lastScrollY.current = window.scrollY;
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (!selectedCategory || categories[selectedCategory] === undefined) {
            return;
        }

        Cookies.set(SELECTED_SECTION_COOKIE, String(categories[selectedCategory]), { expires: 7 });
    }, [categories, selectedCategory]);

    const searchPrefix = activeCatalogKind === PREORDER_CATALOG_KIND ? '/shopping/preorder' : '/shopping';
    const searchAction = selectedTab ? `${searchPrefix}/${getTabRouteSlug(selectedTab)}/search` : `${searchPrefix}/search`;
    const getTabHref = (tab) => normalizeUrl(tab?.Url) || `/shopping/${getTabRouteSlug(tab)}/items`;

    const handleSectionClick = (tab) => {
        const routeSlug = getTabRouteSlug(tab);
        if (!routeSlug || categories[routeSlug] === undefined) {
            return;
        }

        Cookies.set(SELECTED_SECTION_COOKIE, String(categories[routeSlug]), { expires: 7 });
        window.location.assign(getTabHref(tab));
    };

    return (
        <header className={`header mg-header ${headerHidden ? 'mg-header--hidden' : ''}`}>
            <div className="mg-wrapper">
                <div className="mg-header__top">
                    <nav className="mg-header__primary-nav" aria-label="Основные категории">
                        <ul className="nav-links">
                            {tabs.map((tab) => {
                                const routeSlug = getTabRouteSlug(tab);
                                const isSelected = routeSlug === getTabRouteSlug(selectedTab);

                                return (
                                    <li className="mg-header__nav-item" key={routeSlug}>
                                        <button
                                            type="button"
                                            className={`nav-button ${isSelected ? 'active' : ''}`}
                                            onClick={() => handleSectionClick(tab)}
                                            aria-current={isSelected ? 'page' : undefined}
                                        >
                                            {tab.Title}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </nav>

                    <a href="/" className="mg-header__logo" aria-label="На главную">
                        <img alt="Логотип Merkuls" src="/img/svg/merkuls_logo.svg" />
                    </a>

                    <div className="mg-header-top-actions">
                        <button
                            className="mg-header-search-toggle"
                            type="button"
                            aria-label={mobileSearchOpen ? 'Закрыть поиск' : 'Открыть поиск'}
                            aria-expanded={mobileSearchOpen}
                            onClick={() => setMobileSearchOpen((isOpen) => !isOpen)}
                        >
                            {mobileSearchOpen ? <X /> : <Search />}
                        </button>
                        <ProfileHeader />
                        <FaveHeader />
                        <CartHeader />
                    </div>
                </div>

                <div className="mg-header__main-row">
                    <div className="mg-header__nav-stack">
                        {selectedSubTabs.length > 0 && (
                            <nav className="mg-header__secondary-nav" aria-label="Подкатегории">
                                <ul className="mg-header__subnav-links">
                                    {selectedSubTabs.map((subtab) => {
                                        const routeSlug = (getTabRouteSlug(subtab) || '').toLowerCase();
                                        const isSelected = routeSlug === categoryPathFromPath;

                                        return (
                                            <li className="mg-header__subnav-item" key={subtab.Url || routeSlug}>
                                                <a
                                                    className={`mg-header__subnav-link ${isSelected ? 'active' : ''}`}
                                                    href={normalizeUrl(subtab.Url) || '#'}
                                                    aria-current={isSelected ? 'page' : undefined}
                                                >
                                                    {subtab.Title}
                                                </a>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </nav>
                        )}
                    </div>

                    <form className={`search-form mg-header-search__form ${mobileSearchOpen ? 'is-open' : ''}`} action={searchAction} method="get">
                        <div className="mg-header-search__field">
                            <input
                                id="header-search"
                                type="text"
                                placeholder="Поиск по брендам, категориям и товарам"
                                aria-label="Поиск по каталогу"
                                className="mg-header-search__input"
                                name="q"
                            />
                            <button className="search-button" type="submit" aria-label="Найти">
                                <Search size={18} aria-hidden="true" />
                            </button>
                        </div>
                    </form>
                </div>

            </div>
        </header>
    );
};

export default Header;
